import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, User, Wrench } from "lucide-react";

export function Register() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "professional" ? "professional" : "client");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("La password deve avere almeno 8 caratteri"); return; }
    setLoading(true);
    try {
      const user = await register({ ...form, phone: form.phone || null, role });
      toast.success(`Benvenuto su WhooNow, ${user.name}!`);
      navigate(role === "professional" ? "/pro/onboarding" : "/home", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 pt-10 sm:pt-16 space-y-8 sm:max-w-md sm:mx-auto" data-testid="register-page">
      <div className="space-y-2">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Crea il tuo account</h1>
        <p className="text-base text-slate-600">Un solo account per Web, iOS e Android.</p>
      </div>

      <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-full" data-testid="role-selector">
        {[
          { v: "client", label: "Cliente", icon: User },
          { v: "professional", label: "Professionista", icon: Wrench },
        ].map(({ v, label, icon: Icon }) => (
          <button key={v} type="button" data-testid={`role-${v}`} onClick={() => setRole(v)}
            className={`flex items-center justify-center gap-2 h-11 rounded-full text-sm font-semibold transition-colors active:scale-[0.98] ${
              role === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="space-y-2">
          <Label htmlFor="name">Nome e cognome</Label>
          <Input id="name" data-testid="register-name-input" required value={form.name} onChange={set("name")}
            className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="Mario Rossi" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="register-email-input" type="email" required autoComplete="email" value={form.email} onChange={set("email")}
            className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="nome@email.it" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Telefono (opzionale)</Label>
          <Input id="phone" data-testid="register-phone-input" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")}
            className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="+39 333 1234567" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="register-password-input" type="password" required autoComplete="new-password" value={form.password} onChange={set("password")}
            className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="Minimo 8 caratteri" />
        </div>
        <Button data-testid="register-submit-btn" type="submit" disabled={loading}
          className="w-full h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-heading font-semibold text-base active:scale-[0.98] transition-transform">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : role === "professional" ? "Registrati come professionista" : "Registrati"}
        </Button>
      </form>

      <Button data-testid="google-register-btn" variant="outline" onClick={loginWithGoogle}
        className="w-full h-14 rounded-full font-semibold active:scale-[0.98] transition-transform">
        Continua con Google
      </Button>

      <p className="text-sm text-slate-600 text-center pb-6">
        Hai già un account? <Link to="/login" data-testid="go-login-link" className="text-whoo font-semibold">Accedi</Link>
      </p>
    </div>
  );
}
